import { motion } from "framer-motion";

/**
 * SkyThemeBackground
 * The shared 8K animated UFO backdrop used on both the Landing Page and the
 * Dashboard so the experience stays visually cohesive across the app.
 *
 * Props:
 *  - currentTheme: theme key (e.g. "crystal", "frosted", "apocalypse")
 *  - activeTheme: resolved theme object { image, blur, gradient, overlay, accent }
 *  - dim: optional 0..1 extra darkening for content-heavy screens (default 0)
 */
const SkyThemeBackground = ({ currentTheme, activeTheme, dim = 0 }) => {
  if (!activeTheme) return null;

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {/* Main 8K Image */}
      <motion.div
        key={currentTheme}
        initial={{ opacity: 0, scale: 1.1 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${activeTheme.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
          filter: activeTheme.blur,
        }}
      />

      {/* Glass Overlay Effect */}
      <div
        className="absolute inset-0"
        style={{
          background: activeTheme.gradient,
          backdropFilter:
            currentTheme === "frosted"
              ? "blur(12px) saturate(180%)"
              : currentTheme === "holographic"
              ? "blur(4px) saturate(150%) hue-rotate(5deg)"
              : "blur(0px) saturate(120%)",
        }}
      />

      {/* Color Overlay */}
      <div className="absolute inset-0" style={{ background: activeTheme.overlay }} />

      {/* Optional extra dim for readability on busy screens */}
      {dim > 0 && (
        <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${dim})` }} />
      )}

      {/* Animated Light Beam Effect */}
      <motion.div
        animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.05, 1] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse at 50% 30%, ${activeTheme.accent}15 0%, transparent 60%)`,
        }}
      />

      {/* Pulsing Glow Ring */}
      <motion.div
        animate={{ scale: [0.8, 1.2, 0.8], opacity: [0.1, 0.25, 0.1] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full"
        style={{
          background: `radial-gradient(circle, ${activeTheme.accent}30 0%, transparent 70%)`,
          filter: "blur(40px)",
        }}
      />

      {/* Scanning Line Animation */}
      <motion.div
        animate={{ y: ["-100%", "200%"] }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        className="absolute left-0 right-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${activeTheme.accent}60, transparent)`,
          boxShadow: `0 0 20px 5px ${activeTheme.accent}40`,
        }}
      />

      {/* Moving Stars Background */}
      {Array.from({ length: 30 }, (_, i) => ({
        left: (i * 13 + 7) % 100,
        top: (i * 17 + 3) % 100,
        size: 1 + (i % 3),
        duration: 2 + (i % 5),
        delay: i * 0.2,
      })).map((star, i) => (
        <motion.div
          key={`star-${i}`}
          className="absolute rounded-full"
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            width: star.size,
            height: star.size,
            backgroundColor: "#fff",
          }}
          animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1.2, 0.8] }}
          transition={{ duration: star.duration, repeat: Infinity, delay: star.delay }}
        />
      ))}

      {/* Floating Light Orbs */}
      {Array.from({ length: 5 }, (_, i) => ({
        left: 10 + i * 20,
        duration: 8 + i * 2,
        delay: i * 1.5,
      })).map((orb, i) => (
        <motion.div
          key={`orb-${i}`}
          className="absolute w-4 h-4 rounded-full"
          style={{
            left: `${orb.left}%`,
            bottom: "10%",
            background: `radial-gradient(circle, ${activeTheme.accent}80, transparent)`,
            filter: "blur(4px)",
          }}
          animate={{ y: [0, -300, -500], x: [0, 30, -20, 40], opacity: [0, 0.8, 0], scale: [0.5, 1.5, 0.5] }}
          transition={{ duration: orb.duration, repeat: Infinity, delay: orb.delay, ease: "easeInOut" }}
        />
      ))}

      {/* Corner Glow Effects */}
      <motion.div
        animate={{ opacity: [0.1, 0.3, 0.1] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-0 left-0 w-1/3 h-1/3"
        style={{ background: `radial-gradient(ellipse at 0% 0%, ${activeTheme.accent}20 0%, transparent 70%)` }}
      />
      <motion.div
        animate={{ opacity: [0.1, 0.25, 0.1] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        className="absolute bottom-0 right-0 w-1/3 h-1/3"
        style={{ background: `radial-gradient(ellipse at 100% 100%, ${activeTheme.accent}20 0%, transparent 70%)` }}
      />

      {/* Floating Particles Rising */}
      {Array.from({ length: 20 }, (_, i) => ({
        left: (i * 5 + 2) % 100,
        size: 1 + (i % 2),
        duration: 5 + (i % 4),
        delay: i * 0.3,
      })).map((particle, i) => (
        <motion.div
          key={`particle-${i}`}
          className="absolute rounded-full"
          style={{
            left: `${particle.left}%`,
            bottom: "0%",
            width: particle.size,
            height: particle.size,
            backgroundColor: `${activeTheme.accent}`,
          }}
          animate={{ y: [0, -800], opacity: [0, 0.6, 0] }}
          transition={{ duration: particle.duration, repeat: Infinity, delay: particle.delay, ease: "linear" }}
        />
      ))}

      {/* Theme-specific Effects */}
      {(currentTheme === "abduction" || currentTheme === "invasion" || currentTheme === "encounter") && (
        <motion.div
          animate={{ opacity: [0.05, 0.15, 0.05] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0"
          style={{ background: `radial-gradient(ellipse at 50% 20%, ${activeTheme.accent}25 0%, transparent 50%)` }}
        />
      )}

      {currentTheme === "apocalypse" && (
        <motion.div
          animate={{ opacity: [0.1, 0.3, 0.1], backgroundPosition: ["0% 0%", "100% 100%"] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="absolute inset-0"
          style={{ background: "radial-gradient(circle at 70% 30%, rgba(255,100,0,0.2) 0%, transparent 40%)" }}
        />
      )}
    </div>
  );
};

export default SkyThemeBackground;
